import type { StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import type { RoutineEvent } from '@models/routine';

import { parseArgs } from '@utils/apollo';
import { createDateAsUTC } from '@utils/date';

import {
  useGetRoutineInstanceStatusQuery,
  usePostRoutineInstanceStatusMutation,
  useDeleteRoutineInstanceStatusMutation,
} from 'generated/routine.generated';
import type { RoutineInstanceStatusModel, Reference } from 'generated/types';

import { useSession } from './useSession';

export default function useRoutines() {
  const {
    state: { activeProfile },
  } = useSession();
  const vitallPatientId = activeProfile?.vitall_patient_id;

  const { data, loading } = useGetRoutineInstanceStatusQuery({
    variables: {
      input: {
        vitall_patient_id: vitallPatientId ?? '',
      },
    },
    skip: !vitallPatientId,
  });

  const [postRoutineInstance] = usePostRoutineInstanceStatusMutation({
    update: (cache, { data: response }, { variables }) => {
      if (!response?.postRoutineInstanceStatusRecord) {
        return;
      }
      const storeObject =
        response.postRoutineInstanceStatusRecord as unknown as StoreObject;
      try {
        cache.modify<{ getRoutineInstanceStatusRecord: Reference[] }>({
          fields: {
            getRoutineInstanceStatusRecord: (
              existing,
              { storeFieldName, toReference }
            ) => {
              const parsed = parseArgs(storeFieldName);
              const ref = toReference(storeObject);
              if (
                parsed?.vitall_patient_id ===
                  variables?.input.vitall_patient_id &&
                ref
              ) {
                return [...existing, ref];
              }
              return existing;
            },
          },
        });
      } catch (e) {
        if (isDevelopment) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      }
    },
  });
  const [deleteRoutineInstance] = useDeleteRoutineInstanceStatusMutation({
    update: (cache, { data: response }, { variables }) => {
      if (
        !response?.deleteRoutineInstanceStatusRecord.success ||
        !variables?.input.instance_id
      ) {
        return;
      }
      const { instance_id } = variables.input;
      try {
        const normalizedId = cache.identify({
          instance_id,
          __typename: 'RoutineInstanceStatusModel',
        });
        cache.evict({ id: normalizedId });
        cache.gc();
      } catch (e) {
        if (isDevelopment) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      }
    },
  });

  const completedRoutines = data?.getRoutineInstanceStatusRecord.reduce<
    Record<string, RoutineInstanceStatusModel[]>
  >((acc, curr) => {
    const { task_id, status, created_at } = curr;
    if (!task_id) {
      return acc;
    }
    if (!(task_id in acc)) {
      acc[task_id] = [curr];
    } else if (status === 'complete' && created_at) {
      // Only track completed routines
      acc[task_id].push(curr);
    }
    return acc;
  }, {});

  function isRoutineComplete(routineEvent?: RoutineEvent | null) {
    const id = routineEvent?.id;
    if (!id || loading) {
      return false;
    }
    const { startDate } = routineEvent;
    const doneDates = (completedRoutines?.[id] ?? []).map(
      ({ created_at }) => created_at
    );
    return doneDates.includes(
      createDateAsUTC(new Date(startDate)).toISOString()
    );
  }

  async function completeRoutine(routine: RoutineEvent) {
    const { id } = routine;
    if (!id || loading) return;

    if (isRoutineComplete(routine)) {
      const instances = completedRoutines?.[id] ?? [];
      const date = createDateAsUTC(new Date(routine.startDate)).toISOString();
      await Promise.all(
        instances
          .filter(({ created_at }) => created_at === date)
          .map(async ({ instance_id }) =>
            deleteRoutineInstance({
              variables: {
                input: {
                  instance_id: instance_id ?? '',
                },
              },
            })
          )
      );
    } else {
      await postRoutineInstance({
        variables: {
          input: {
            created_at: createDateAsUTC(
              new Date(routine.startDate)
            ).toISOString(),
            status: 'complete',
            task_id: id.toString(),
            vitall_patient_id: vitallPatientId,
          },
        },
      });
    }
  }

  return { isRoutineComplete, completeRoutine };
}
